import Box from '@mui/material/Box';
import { FormattedMessage } from 'react-intl';

const CheckIcon = () => (
  <svg
    width={12}
    height={12}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={3}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const ChevronIcon = () => (
  <svg
    width={15}
    height={15}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 6l6 6-6 6" />
  </svg>
);

const stepCircleSx = {
  width: 20,
  height: 20,
  borderRadius: '50%',
  fontSize: 12,
  fontWeight: 700,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 'none',
} as const;

/**
 * Sticky 2-step checkout nav ("Confirm your plan > Payment and Billing") from
 * the Payment & billing v1 design mock. On step 2 the first step renders as a
 * completed checkmark and navigates back to the plan page.
 */
export const PlanStepNav = (
  { currentStep, onBackToPlan }: {
    currentStep: 1 | 2;
    onBackToPlan?: () => void;
  },
) => (
  <Box
    id="plan-v2-step-nav"
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.125,
      height: 56,
      px: 2.75,
      mx: -5,
      backgroundColor: '#fff',
      borderBottom: '1px solid #ececf4',
      position: 'sticky',
      top: 0,
      zIndex: 30,
      fontSize: 13.5,
      fontFamily: 'Inter, sans-serif',
    }}
  >
    {currentStep === 1 ? (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          color: '#16141d',
          fontWeight: 600,
        }}
      >
        <Box component="span" sx={{ ...stepCircleSx, backgroundColor: '#16141d', color: '#fff' }}>
          1
        </Box>
        <FormattedMessage id="page.plan.v2.header" />
      </Box>
    ) : (
      <Box
        role="button"
        tabIndex={0}
        onClick={onBackToPlan}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            onBackToPlan?.();
          }
        }}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          color: '#73718a',
          fontWeight: 500,
          cursor: 'pointer',
        }}
      >
        <Box component="span" sx={{ ...stepCircleSx, backgroundColor: '#16141d', color: '#fff' }}>
          <CheckIcon />
        </Box>
        <FormattedMessage id="page.plan.v2.header" />
      </Box>
    )}
    <Box component="span" sx={{ display: 'inline-flex', color: '#cdcadf' }}>
      <ChevronIcon />
    </Box>
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        color: currentStep === 2 ? '#16141d' : '#b5b3c4',
        fontWeight: currentStep === 2 ? 600 : 400,
      }}
    >
      <Box
        component="span"
        sx={currentStep === 2
          ? { ...stepCircleSx, backgroundColor: '#16141d', color: '#fff' }
          : { ...stepCircleSx, border: '1.5px solid #d4d2e0', color: '#b5b3c4' }}
      >
        2
      </Box>
      <FormattedMessage id="page.plan.v2.step.payment.billing" />
    </Box>
  </Box>
);
